import React, { useCallback, useMemo, useState } from 'react';
import { ANALYSIS_TYPE_NAMES } from 'config/labels';
import { Series } from '@models/Series';
import { AnalysisType } from 'config/analysisConfig';
import { useAnalysisPage } from '../useAnalysisPage';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useTool } from '@hooks/Tool/useTool';
import { TOOL_REGISTRY } from 'config/toolRegistry';
import styles from './AnalysisTool.module.scss';
import GroupContextMenu from '@components/ContextMenu/GroupContextMenu';
import { useBufferPool } from '../rendering/Panels/Renderers/vtkManagers';
import { configStore } from 'config/configStore';
import { useConfig } from 'config/useConfig';
import { getConfiguredCprCrossSectionImageMode } from '../rendering/Panels/cprImageModeConfig';


import { ICON_MAP } from '@components/Icons/Icons';


export interface AnalysisToolProps {
  seriesList: Series[];
  analysisStatusByName?: Map<string, boolean | null>;
}
// interface AnalysisToolProps {
//   title: string | undefined;
//   tools: ToolCategoryConfig | undefined;
// }

// Title이 (선택한 Analysis) + (선택한 Panel) 로 가면 혼동이 덜 될까?
// 이미 Tool 위에 위치한 List에서 선택한 Analysis의 이름이 Highlight되므로 앞에는 빼도 될까?
function AnalysisTool({
  seriesList,
  analysisStatusByName,
}: AnalysisToolProps): React.JSX.Element {
  const CameraIcon = ICON_MAP['camera'];
  const { selectedSeries, selectedSeriesId } = useBrowserPage();
  const {
    selectedAnalysis,
    selectedLayoutConfig,
    updateCurrentLayout,
    globalToolState,
  } = useAnalysisPage();
  const {
    handleToolActivate,
    getVisibleToolsForContext,
    getGroupedToolsForMenu,
  } = useTool();


  const { config } = useConfig();
  const type: AnalysisType =
    selectedAnalysis && selectedAnalysis.analysisType
      ? selectedAnalysis.analysisType
      : 'empty';
  const isEditMode = config.EditMode !== null;
  const ffrReady =
    type === 'FFR' ? analysisStatusByName?.get('ct-ffr') === true : true;

  const groupedTools = getGroupedToolsForMenu();
  const visibleTools = getVisibleToolsForContext();
  const visibleToolIds = visibleTools.map((t) => t.id);
  const invisibleToolIds = useMemo(() => {
    return TOOL_REGISTRY.map((t) => t.id).filter(
      (id) => !visibleToolIds.includes(id),
    );
  }, [visibleToolIds]);

  const isLesionStackFeatureAvailable =
    type === 'plaque' && getConfiguredCprCrossSectionImageMode() === 'server';
  const isLesionStackViewLayout =
    isLesionStackFeatureAvailable &&
    selectedLayoutConfig.type === 'lesionStackView';

  const autoCaptureGroups = useMemo(() => {
    if (!groupedTools?.autocapture) return [];

    const filteredTools = groupedTools.autocapture.filter((tool) => {
      if (type !== 'plaque') return true;

      return isLesionStackViewLayout
        ? tool.id === 'AutoCapture_Plaque_LesionStackCapture'
        : tool.id !== 'AutoCapture_Plaque_LesionStackCapture';
    });

    return [
      {
        groupName: 'Auto Capture',
        toggleEnabled: false,
        items: filteredTools.map((tool) => ({
          label: tool.name,
          onClick: () => handleToolActivate(tool.id),
          isDisabled: !visibleToolIds.includes(tool.id),
        })),
      },
    ];
  }, [
    groupedTools,
    handleToolActivate,
    isLesionStackViewLayout,
    type,
    visibleToolIds,
  ]);

  const singleTools = useMemo(() => {
    return TOOL_REGISTRY.filter((tool) =>
      ['Report', 'Edit', 'ToggleSegmentationManager'].includes(tool.id),
    );
  }, [type]);

  const [showAutoCaptureMenu, setShowAutoCaptureMenu] = useState(false);

  const hasOriLabelData = useMemo(() => {
    return configStore.get().isLowSegReady;
  }, [configStore.get()]);

  const quality = configStore.get().generalSetting.renderingQuality;

  const blockAutoCapture =
    type === 'MPR' ||
    type === 'empty' ||
    type === 'aorticValveCalcium' ||
    type === 'tavi' ||
    type === 'calciumScore' ||
    type === 'ejectionFraction' ||
    type === 'fai' ||
    type === 'isr' ||
    type === 'myocardial' ||
    type === 'wallthickness' ||
    type === 'stenosis' ||
    type === 'preview' ||
    (quality === 'low' && !hasOriLabelData);

  const handleToggleMenu = useCallback(() => {
    if (isEditMode) return;

    if (type === 'FFR') {
      if (selectedLayoutConfig.type !== 'ffr-capture') {
        updateCurrentLayout('ffr-capture');
      } else {
        updateCurrentLayout('ffr');
      }
      return;
    }

    if (blockAutoCapture) return;
    if (!autoCaptureGroups.length) return;

    const items = autoCaptureGroups[0].items;

    if (
      items.length === 1 &&
      !items[0].isDisabled &&
      !isLesionStackViewLayout
    ) {
      items[0].onClick();
      return;
    }

    setShowAutoCaptureMenu((prev) => !prev);
  }, [
    autoCaptureGroups,
    blockAutoCapture,
    isEditMode,
    type,
    updateCurrentLayout,
    selectedLayoutConfig.type,
    isLesionStackViewLayout,
  ]);

  return (
    <div className={styles.container}>
      <span className={styles.name}>
        {`${ANALYSIS_TYPE_NAMES[selectedSeries?.modality ?? 'empty'][type]} Tool`}
      </span>
      <div className={styles.toolbar}>
        <div className={styles.tools}>
          <div
            data-context-menu-trigger="true"
            className={`${styles.frames} ${
              blockAutoCapture || isEditMode|| !ffrReady ? styles.disabled : ''
            }`}
            onClick={handleToggleMenu}
          >
            <div className={`${styles.item}`}>
              <CameraIcon className={styles.icon} />
              <span className={styles.text}>
                {type === 'FFR' ? 'Capture Mode' : 'Auto Capture'}
              </span>
            </div>
          </div>
          {singleTools.map((tool) => {
            const Icon = tool.icon ? ICON_MAP[tool.icon] : undefined;
            const isEditWaiting =
              tool.id === 'Edit' && selectedSeriesId && config.modifyRun;
            const isPlaqueManualActive =
              tool.id === 'TogglePlaqueManual' &&
              (globalToolState?.toggles.TogglePlaqueManual ||
                selectedLayoutConfig.type === 'plaqueManuel');

            const isFFRLayout =
              !!selectedLayoutConfig.type &&
              ['ffr-capture', 'ffr'].includes(selectedLayoutConfig.type) &&
              type === 'FFR';

            const isSegmentationManagerDisabled =
              tool.id === 'ToggleSegmentationManager' &&
              (isFFRLayout || isEditMode);

            const isReportDisabledForLesionStack =
              tool.id === 'Report' && isLesionStackViewLayout;

            const isToolDisabled =
              invisibleToolIds.includes(tool.id) ||
              isSegmentationManagerDisabled ||
              isReportDisabledForLesionStack ||
              config.EditMode ||
              isEditWaiting ||
              (tool.id !== 'Edit' && !ffrReady);

            return (
              <div
                key={tool.id}
                className={`${styles.frames} ${
                  isToolDisabled ? styles.disabled : ''
                } ${isPlaqueManualActive ? styles.active : ''}
                ${isEditWaiting ? styles.waiting : ''}`}
                onClick={() => {
                  if (isToolDisabled) return;
                  handleToolActivate(tool.id);
                }}
              >
                <div className={`${styles.item}`}>
                  {Icon && (
                    <span className={styles.icon}>
                      <Icon />
                    </span>
                  )}
                  <span className={styles.text}>
                    {tool.name}
                    {isEditWaiting && (
                      <>
                        <span className={styles.dot}>.</span>
                        <span
                          className={styles.dot}
                          style={{ animationDelay: '0.1s' }}
                        >
                          .
                        </span>
                        <span
                          className={styles.dot}
                          style={{ animationDelay: '0.2s' }}
                        >
                          .
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showAutoCaptureMenu && (
        <GroupContextMenu
          groups={autoCaptureGroups}
          onClose={() => setShowAutoCaptureMenu(false)}
        />
      )}
    </div>
  );
}

export default AnalysisTool;
